import styled from 'styled-components';
import Helmet from 'react-helmet';
import React, { useState } from 'react';
import { useTranslate } from '../contexts/languageContext';
import { useIsDesktop, useIsMobile } from '../hooks/useMediaQuery';
import useIni from '../hooks/useIni';
import {
  responsive,
  nombreEleccion,
  abbrEleccionACodigoEleccion,
} from '../resources/constantes.json';
import { useSetPage } from '../contexts/pageContext';
import { Link } from 'react-router-dom';

import qrAndroid from '../assets/img/qr-google-play.png';
import qriOS from '../assets/img/qr-app-store.png';
import logoAndroid from '../assets/img/logo-google-play.png';
import logoIOS from '../assets/img/logo-app-store.png';
import bgInicio from '../assets/img/bg-inicio.svg';
import LogoRegistraduria from '../assets/img/logoRegisBlanco.png';
import LogoEleccion from '../assets/img/logoEleccionesIni.png';
import { useNomenclator } from '../contexts/nomenclatorContext';

/**
 * Página que sirve de inicio y que muestra un resumen de resultados para todas las elecciones
 */

const Inicio = ({ nomenclator }) => {
  const t = useTranslate();
  const isDesktop = useIsDesktop();
  const ini = useIni();
  const items = ini?.botones;
  const isClosed = ini?.isClosed;
  return (
    <LayoutInicio ini={ini}>
      {ini && (
        <Content>
          {isClosed ? (
            <TextContainer>
              <TextClosed>{t('inicioClose')}</TextClosed>
            </TextContainer>
          ) : (
            <LinkContainer desktop={isDesktop}>
              <ListaLinks items={items.filter(i => i.hab === 'activado')} />
            </LinkContainer>
          )}
        </Content>
      )}
    </LayoutInicio>
  );
};

export function LayoutInicio({ children, ini }) {
  const t = useTranslate();
  const isDesktop = useIsDesktop();
  const appsVisibles = ini?.appsVisibles;
  return (
    <>
          <Helmet>
        <title itemProp="name" lang="es">
        Elecciones de Congreso de la República y Consultas populares interpartidistas
        </title>
      </Helmet>
      <MainLayout>
        <SLogoRegistraduria>
          <img src={LogoRegistraduria} alt={'Logo Registraduria'} />
        </SLogoRegistraduria>
        <MainContainer>
          <SLogoEleccion>
            <img src={LogoEleccion} alt={'Logo elección'} />
          </SLogoEleccion>
          <MainTitle>
            <span>{t('tituloMainInicio1')}</span>
            <span>{t('tituloMainInicio2')}</span>
          </MainTitle>
          <Separator />
          <Notes>
            <p>{t('footer2')}</p>
          </Notes>
          {children}
          <AppsContainer isDesktop={isDesktop}>
            <QrText>{t('qrText')}</QrText>
            <QrBlock isDesktop={isDesktop} visible={appsVisibles} />
          </AppsContainer>          
        </MainContainer>
      </MainLayout>
    </>
  );
}

const ListaLinks = ({ items }) => {
  const nomenclator = useNomenclator();
  const richItems = items.filter(item => nomenclator.nomAmbitos[item.id] !== undefined).map(i => ({
    id: i.id,
    ...nomenclator.nomAmbitos[i.id],
  }));
  richItems.sort((a,b) => nomenclator.ordAmbitos.indexOf(a.id) - nomenclator.ordAmbitos.indexOf(b.id));
  return (
    <ListaUlWrapper>
      {richItems.map(item => (
        <SLink to={nombreEleccion[item.id] + '/0'}>{item.n}</SLink>
      ))}
    </ListaUlWrapper>
  );
};

export function QrBlock({ isDesktop, visible = true }) {
  const t = useTranslate();
  return (
    <>
      {visible && (
        <QrContainer>
          <AppBlock isDesktop={isDesktop}>
            {isDesktop && (
              <QRImage src={qriOS} alt={t('qriOS')} element="right" />
            )}
            <a rel="noopener noreferrer" target="_blank" href={'https://apps.apple.com/us/app/elecciones-colombia-2022/id1611570201'}>
              <ShopLogo src={logoIOS} alt="App Store" element="right" />
            </a>
          </AppBlock>
          <AppBlock isDesktop={isDesktop}>
            {isDesktop && (
              <QRImage src={qrAndroid} alt={t('qrAndroid')} element="left" />
            )}
            <a rel="noopener noreferrer" target="_blank" href={'https://play.google.com/store/apps/details?id=co.registraduria.gov.elecciones.congreso.colombia2022'}>
              <ShopLogo src={logoAndroid} alt="Google Play" element="left" />
            </a>
          </AppBlock>
        </QrContainer>
      )}
    </>
  );
}

const MainLayout = styled.main`
  background-image: url(${bgInicio});
  background-size: cover;
  height: 100%;
  min-height: 100vh;
  display: block;
  @media screen and (max-width: ${responsive.tablet}px) {
    display: flex;
    flex-direction: column-reverse;
  }
}
`;
const SLogoRegistraduria = styled.div`
  display block;
  img{ 
    height: 115px;
    @media screen and (max-width: ${responsive.tablet}px) {
      margin: auto;
      display: block;
      height: 100px;
    }

  }
`;

const TextContainer = styled.div`
  text-align: left;
  font-size: 2rem;
  margin-bottom: 14px;
  color: #2f435f;
  font-weight: 400;
`;
const Notes = styled.div`
  box-sizing: border-box;
  padding: 0 0 0.5rem 0;
  font-size: 1.3rem;
  color: #fff;
  width:60%;
  text-align: center;
`;

/*
 *  Main layout
 */

const MainContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
`;

const SLogoEleccion = styled.div`
  img {
    height: 225px;
    @media screen and (max-width: ${responsive.tablet}px) {
      height: 160px;
    }
  }
`;

const MainTitle = styled.div`
  display: flex;
  flex-direction: column;
  font-family: Lato;
  text-align: center;
  line-height: initial;
  color: #ffffff;
  span:first-child {
    font-weight: 100;
    font-size: 28px;
    @media screen and (max-width: ${responsive.tablet}px) {
      font-size: 18px;
      width: 90%;
      margin: 0 auto;
    }
  }
  span:nth-child(2) {
    font-weight: 400;
    font-size: 32px;
    @media screen and (max-width: ${responsive.tablet}px) {
      font-size: 24px;
      width: 90%;
      margin: 0 auto;
    }
  }
`;

const TextClosed = styled.p`
  font-family: Lato;
  font-weight: 400;
  font-size: 2rem;
  color: #01f0ea;
`;

const Separator = styled.span`
  border: 1px solid #01f0ea;
  height: 0;
  width: 75%;
  margin: 15px 0;
`;

const Content = styled.div``;

/*
 * Links
 */

const LinkContainer = styled.div`
  text-align: left;
  margin: "2rem 5%"
  font-weight: 700;
  font-size: 1.8rem;
`;

const ListaUlWrapper = styled.ul`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 2rem 10rem;
  @media (max-width: ${responsive.tablet}px) {
    margin-top: 30px;
    display: block;
  }
`;

const SLink = styled(Link)`
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  color: #ffff;
  background-color: #04aa94;
  border-radius: 3px;
  padding-top: 12px;
  padding-bottom: 12px;
  padding-left: 20px;
  padding-right: 20px;
  @media (max-width: ${responsive.tablet}px) {
    margin-bottom: 5px;
  }
`;

/*
 * APPS
 */

const AppsContainer = styled.div`
  width: 50%;
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 15px;
  padding: 1rem 0;
  @media (max-width: ${responsive.tablet}px) {
    width: 70%;
  }
`;

const QrContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin: 0.5rem 3rem;
  @media (max-width: ${responsive.tablet}px) {
    justify-content: space-between;
  }
`;

const AppBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 105px;
  @media (max-width: ${responsive.tablet}px) {
    margin-right: 0px;
  }
`;

const QRImage = styled.img`
  height: 150px;
  width: 150px;
`;

const ShopLogo = styled.img`
  height: 30px;
  margin-top: 0.5rem;
`;

const QrText = styled.span`
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  text-align: center;
  color: #172946;
`;

/**************************************************/
export default Inicio;
